/**
 * Base de datos IndexedDB con Dexie
 *
 * Almacena el historial de protocolos completados para:
 * - Persistir datos entre sesiones
 * - Funcionar offline (PWA)
 * - Sincronizar con Supabase cuando hay conexión
 */

import Dexie, { type Table } from 'dexie';
import type { CompletionRecord, DaySnapshot } from '@/core/types';

/**
 * Esquema de la base de datos Exocortex
 */
export class ExocortexDB extends Dexie {
  // Tablas
  completions!: Table<CompletionRecord>;
  daySnapshots!: Table<DaySnapshot>;

  constructor() {
    super('ExocortexDB');

    // Versión 1: Esquema inicial
    this.version(1).stores({
      // CompletionRecord: índices para queries comunes
      completions: [
        'id',           // Clave primaria
        'date',         // Para agrupar por día
        'protocolId',   // Para estadísticas por protocolo
        'layer',        // Para filtrar por capa
        'frequency',    // Para filtrar por frecuencia
        'completedAt',  // Para ordenar cronológicamente
        'syncStatus',   // Para encontrar pendientes de sync
        '[date+protocolId]', // Índice compuesto para evitar duplicados
      ].join(', '),

      // DaySnapshot: un registro por día
      daySnapshots: [
        'date',         // Clave primaria (YYYY-MM-DD)
        'year',         // Para queries por año
        'month',        // Para queries por mes
        'weekOfYear',   // Para queries por semana
        'syncStatus',   // Para encontrar pendientes de sync
        '[year+month]', // Índice compuesto para rangos
      ].join(', '),
    });
  }
}

// Instancia singleton de la base de datos
export const db = new ExocortexDB();

/**
 * Helpers para operaciones comunes
 */

/**
 * Obtener fecha actual en formato YYYY-MM-DD
 */
export function getTodayDate(): string {
  const dateStr = new Date().toISOString().split('T')[0];
  return dateStr ?? new Date().toISOString().substring(0, 10);
}

/**
 * Obtener número de semana ISO del año
 */
export function getWeekOfYear(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/**
 * Generar ID único para registros
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Crear un DaySnapshot vacío para una fecha
 */
export function createEmptyDaySnapshot(date: string): DaySnapshot {
  const d = new Date(date);
  const dayOfWeek = d.getDay();

  return {
    date,
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    weekOfYear: getWeekOfYear(d),
    dayOfWeek,
    isWeekend: dayOfWeek === 0 || dayOfWeek === 6,

    survival: { total: 0, completed: 0, percentage: 0, completedIds: [] },
    maintenance: { total: 0, completed: 0, percentage: 0, completedIds: [] },
    growth: { total: 0, completed: 0, percentage: 0, completedIds: [] },

    totalProtocols: 0,
    completedProtocols: 0,
    completionRate: 0,
    totalTimeEstimate: 0,
    completedTimeEstimate: 0,

    usedHardDayMode: false,
    usedWeekendMode: false,

    syncStatus: 'pending',
    lastModified: new Date().toISOString(),
  };
}

/**
 * Verificar si la base de datos está disponible
 */
export async function isDBAvailable(): Promise<boolean> {
  try {
    await db.open();
    return true;
  } catch {
    console.error('IndexedDB no disponible');
    return false;
  }
}

/**
 * Limpiar datos antiguos (más de 2 años)
 * Para evitar que la DB crezca indefinidamente
 */
export async function cleanOldData(): Promise<number> {
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
  const cutoffDate = twoYearsAgo.toISOString().split('T')[0];

  const deletedCompletions = await db.completions
    .where('date')
    .below(cutoffDate)
    .delete();

  const deletedSnapshots = await db.daySnapshots
    .where('date')
    .below(cutoffDate)
    .delete();

  return deletedCompletions + deletedSnapshots;
}
